<template>
  <v-sheet
    class="message-list"
  >
    <v-expansion-panels
      class="ga-3"
      flat
      tile
      hide-actions
      multiple
      :model-value="[initiallyOpened, 'kudos']"
    >
      <!-- `class="mt-0"` prevents panel moving down when expanded -->
      <v-expansion-panel
        v-for="panel in panels"
        :key="panel.id"
        :disabled="isRevisionSaving"
        class="mt-0"
        :value="panel.id"
      >
        <v-expansion-panel-title
          v-slot="{ expanded }"
          class="text-h3"
          :class="{ readonly: panel.isEmpty && !panel.reportCount }"
          height="32"
          min-height="32"
          :readonly="panel.isEmpty && !panel.reportCount"
          static
        >
          <div class="d-flex w-100">
            <v-icon
              v-if="panel.isEmpty && !panel.reportCount"
              class="my-auto mr-1"
              :class="[`text-${panel.id}`]"
              icon="material-symbols:check-circle-outline"
              size="16"
            />
            <v-icon
              v-else
              :icon="expanded ? '$chevron-down' : '$chevron-right'"
              color="iconDefault"
            />

            <div
              class="ml-1 d-flex flex-column justify-center"
              :class="[panel.isEmpty ? 'text-textLight' : `text-${panel.id}`]"
            >
              {{ panel.totalCount || 'No' }} {{ panel.title }}{{ panel.totalCount === 1 ? '' : 's' }}
            </div>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text v-if="!panel?.isEmpty || panel.reportCount > 0">
          <div
            v-for="message in panel.items"
            :key="message.id"
            class="d-flex message-container"
          >
            <GuidanceMessage
              :message="message"
              :show-icon="panel.successIcon"
              :hide-actions="hideActions"
              @add="emit('add', $event)"
              @show-apply-template="emit('show-apply-template', $event)"
              @track-sidebar-event="emit('track-sidebar-event', $event)"
            >
              <template #custom-action="{ message }">
                <slot name="custom-action" v-bind="{ message }" />
              </template>
              <template #append-icon="{ message }">
                <slot name="append-icon" v-bind="{ message }" />
              </template>
            </GuidanceMessage>
          </div>

          <div
            v-if="panel.otherIssues.length > 0"
            class="d-flex py-2 text-caption"
            :class="[`text-${panel.id}`]"
          >
            <v-icon
              class="mr-1"
              icon="mdi:warning-circle"
              size="16"
            />

            <template v-if="panel.otherIssues.length === 1">
              1 {{ panel.items.length === 0 ? '' : 'more' }} in-line issue
            </template>

            <template v-else>
              {{ panel.otherIssues.length }} {{ panel.items.length === 0 ? '' : 'more' }} in-line issues
            </template>
          </div>

          <div
            v-if="panel.reportCount"
            class="d-flex py-2 text-caption report-link"
            :class="[`text-${panel.id}`]"
          >
            <v-icon
              icon="material-symbols-light:flag-circle"
              class="mr-1"
              size="16"
            />

            <span>
              <template v-if="isAdmin">
                <span
                  class="text-decoration-underline cursor-pointer"
                  @click="emit('report-link-click', { panelId: panel.id, documentType, isAdmin })"
                >
                  {{ panel.reportCount }}
                  live{{ documentType === 'job_template' ? ' template' : ', external job' }}{{ panel.reportCount === 1 ? '' : 's' }}
                </span>
                {{ panel.reportCount === 1 ? 'has' : 'have' }}
                {{ panel.id }}
                issues
              </template>
              <template v-else>
                <span
                  class="text-decoration-underline cursor-pointer"
                  @click="emit('report-link-click', { panelId: panel.id, documentType, isAdmin })"
                >
                  {{ panel.reportCount }} of your live, external jobs
                </span> {{ panel.reportCount === 1 ? 'has' : 'have' }} {{ panel.id }}
                issues
              </template>
            </span>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-sheet>
</template>

<script setup>
import { computed } from 'vue';
import GuidanceMessage from './GuidanceMessage.vue';

const emit = defineEmits(['show-apply-template', 'track-sidebar-event', 'add', 'report-link-click']);

const props = defineProps({
  bodyMessages: { type: Object, required: true },
  descriptionJson: { type: Object, default: () => ({}) },
  postSummary: { type: Object, default: () => ({}) },
  isRevisionSaving: { type: Boolean, default: false },
  hideActions: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  openCategory: { type: String, default: '' },
  documentType: { type: String, default: '' },
  hideCategories: { type: Array, default: () => [] },
});

const allSectionsById = computed(() => props.descriptionJson.fields?.reduce((res, field) => {
  field.sections?.forEach((section) => {
    section.blocks?.forEach((block) => {
      block.detected_sections?.forEach((section) => {
        if (!res[section]) {
          res[section] = [];
        }
        res[section].push(block);
      });
    });
  });
  return res;
}, {}));

const messages = computed(() => Object.keys(props.bodyMessages)
  .map((id) => ({
    ...props.bodyMessages[id],
    id,
  }))
  .filter((m) => m.action !== 'suppress')
  .filter((m) => !m.is_nudge || (m.focus_sections || []).some((s) => allSectionsById.value[s])));

const sideBarMessages = computed(() => messages.value
  .filter((m) => (m.action === 'sidebar' || m.action.startsWith('custom')) && !m.is_nudge));

const kudos = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'green-solid'));

const performanceIssues = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'red-solid'));

const brandIssues = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'blue-solid'));

const complianceIssues = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'yellow-solid'));

const setupIssues = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'red-underline'));

const completionIssues = computed(() => sideBarMessages.value
  .filter((m) => m.severity_class === 'blue-underline'));

const uncategorizedIssues = computed(() => sideBarMessages.value
  .filter((m) => !['green-solid', 'red-solid', 'blue-solid', 'yellow-solid', 'red-underline', 'blue-underline']
    .includes(m.severity_class)));

const categories = computed(() => ({
  kudos: {
    items: kudos.value,
    title: 'Kudo',
    successIcon: true,
    otherIssues: [],
    hideEmpty: true,
  },

  performance: {
    items: performanceIssues.value,
    title: 'Job Performance Issue',
    otherIssues: messages.value
      .filter((m) => (m.action !== 'sidebar' || m.is_nudge)
        && m.severity_class === 'red-solid'),
    reportCount: props.documentType === 'job' ? props.postSummary.jobPerformanceJobs : 0,
  },

  brand: {
    items: brandIssues.value,
    title: 'Employer Brand Issue',
    otherIssues: messages.value
      .filter((m) => (m.action !== 'sidebar' || m.is_nudge)
        && m.severity_class === 'blue-solid'),
    reportCount: props.postSummary.employerBrandJobs,
  },

  compliance: {
    items: complianceIssues.value,
    title: 'Compliance Issue',
    otherIssues: messages.value
      .filter((m) => (m.action !== 'sidebar' || m.is_nudge)
        && m.severity_class === 'yellow-solid'),
    reportCount: props.documentType === 'job' ? props.postSummary.nonCompliantJobs : 0,
  },

  setup: {
    items: setupIssues.value,
    title: 'Job Setup Issue',
    otherIssues: [],
    hideEmpty: true,
  },

  completion: {
    items: completionIssues.value,
    title: 'Job Completion Issue',
    otherIssues: [],
    hideEmpty: true,
  },

  uncategorized: {
    items: uncategorizedIssues.value,
    title: 'Other Issues',
    otherIssues: [],
    hideEmpty: true,
  },
}));

const panels = computed(() => ['setup', 'completion', 'compliance', 'brand', 'performance', 'uncategorized', 'kudos']
  .map((id) => ({
    id,
    isEmpty: (categories.value[id].items.length + categories.value[id].otherIssues.length) === 0,
    totalCount: categories.value[id].items.length + categories.value[id].otherIssues.length,
    ...categories.value[id],
  }))
  // hide empty kudos and uncategorized
  .filter((p) => !props.hideCategories.includes(p.id) && (!p.hideEmpty || !p.isEmpty)));

// open first non-empty category
const initiallyOpened = computed(() => [props.openCategory, 'compliance', 'brand', 'performance', 'setup', 'completion']
  .filter((id) => id && panels.value.find((p) => p.id === id)?.isEmpty === false)[0]);
</script>

<style scoped lang="less">
.v-expansion-panel-title {
  padding: 6px 12px 6px 0;
  &.readonly {
    pointer-events: none;
  }
}

.v-expansion-panel-text :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 12px 0 24px;
}

.v-expansion-panel--disabled {
  opacity: 0.5;
  pointer-events: none;
  .v-expansion-panel-title {
    color: transparent;
  }
}

.message-container:not(:last-child) {
  border-bottom: 1px solid @stroke-default;
}

.text-kudos {
  color: @issue-kudos;
}
.text-performance, .text-setup {
  color: @issue-performance;
}
.text-brand, .text-completion {
  color: @issue-brand;
}
.text-compliance {
  color: @issue-compliance;
}
.text-uncategorized {
  color: @grey-700;
}
.report-link {
  a {
    color: inherit;
    text-decoration: underline;
  }
}
</style>
